// Service-page survey forms. Keyed by route slug.
// Local only: ServiceSurveyForm logs submissions to console.log().
// Rendered as ONE short step (ServiceSurveyForm): the CONTACT() fields plus each
// form's `textarea`. The role / budget / scope / how-found questions below are no
// longer shown (simplified 2026-10-20) but are kept so they can be restored.

// T-12.5: "How did you find us?" attribution question. Required on every form.
// Values match the report's spec; stored as `how_found` in the GHL contact record.
export const HOW_FOUND_OPTIONS = [
  'Google search',
  'Google AI Overview / Gemini',
  'ChatGPT',
  'Claude',
  'Perplexity',
  'LinkedIn',
  'Upwork',
  'Fiverr',
  'Referral',
  'Other',
]

export const HOW_FOUND_QUESTION = {
  name: 'howFound',
  label: 'How did you find GHL Prime?',
  type: 'select',
  options: HOW_FOUND_OPTIONS,
}

// Sample value per custom business label, so the hint matches what is asked.
const BUSINESS_HINTS = {
  'Business / product name': 'Acme Analytics',
  'Your SaaS / agency name': 'Acme CRM',
  'Business / app name': 'Acme Fitness',
}

// Step 1 is identical on every form, so the placeholders live here once.
// businessLabel is the only part that varies per service (agency name, app
// name, and so on), and its placeholder follows whatever label it is given.
const CONTACT = (businessLabel) => [
  { name: 'name', label: 'Your name', type: 'text', required: true, placeholder: 'Jane Doe' },
  { name: 'email', label: 'Email address', type: 'email', required: true, placeholder: 'jane@youragency.com' },
  { name: 'phone', label: 'Phone number', type: 'phone', required: true },
  {
    name: 'business',
    label: businessLabel || 'Business name',
    type: 'text',
    required: true,
    placeholder: businessLabel ? `e.g. ${BUSINESS_HINTS[businessLabel] || 'Acme Co.'}` : 'Acme Marketing',
  },
]

export const SERVICE_FORMS = {
  // ---------------------------------------------------------------- GHL Setup
  '/services/ghl-setup': {
    titlePill: 'Free GHL Audit Included',
    titleSub: "Tell us about your setup, we'll map exactly what needs fixing.",
    heroSubhead:
      'A messy GoHighLevel account costs you leads and hours every week. Tell us where you stand and our Certified Admins will map the fix.',
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Scope'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['Agency owner', 'SaaS founder', 'Business owner', 'Freelancer / consultant'] },
      ] },
      { eyebrow: 'STEP 3 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'Tell us what you need help with', placeholder: "Describe your setup, what's broken, any migration, and what you want fixed..." } },
      { eyebrow: 'STEP 4 · SCOPE', questions: [
        { name: 'subaccounts', label: 'How many sub-accounts are you managing?', options: ['Just me / pre-launch', '1–5 clients', '6–20 clients', '20+ clients'] },
      ] },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'One of our GHL Certified Admins will review your situation and reach out to map your setup on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Automation
  '/services/automation': {
    titlePill: 'Free Automation Audit',
    titleSub: "Tell us what slows your team down, we'll tell you exactly what to automate first.",
    heroSubhead:
      "Stop losing leads to slow follow-up and manual busywork. Tell us your setup and we'll show you what to automate first.",
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Budget'],
    submitLabel: 'Get My Automation Plan',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'bizType', label: 'What type of business are you?', options: ['Marketing agency', 'Real estate', 'Coaching / consulting', 'SaaS company', 'Other'] },
      ] },
      { eyebrow: 'STEP 3 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'Describe your current workflow situation', placeholder: "E.g. 'I have a form but nothing fires after submission, and leads never get followed up...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your monthly budget for this?', options: ['$100 – $500', '$500 – $1,500', '$1,500 – $3,000', '$3,000+'] },
      ] },
    ],
    success: {
      title: "We'll map your automation plan on a free call.",
      subtitle: 'A GHL automation specialist will reach out within 1 hour to walk through your highest-impact workflows.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- AI Agent Builder
  '/services/ai-agent-builder': {
    titlePill: 'Free AI Agent Strategy Call',
    titleSub: "Tell us where you're losing leads, we'll show you exactly what to build.",
    heroSubhead:
      "AI agents that qualify leads, answer messages, and book calls 24/7. Tell us where you're losing leads and we'll design the build.",
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Scope'],
    submitLabel: 'Build My AI Agent',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'bizType', label: 'What type of business are you?', options: ['Marketing agency', 'Real estate', 'Coaching / consulting', 'E-commerce / retail', 'SaaS or tech product'] },
      ] },
      { eyebrow: 'STEP 3 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What do you want the AI agent to do?', placeholder: "E.g. 'Qualify inbound WhatsApp leads in English and Spanish and book calls...'" } },
      { eyebrow: 'STEP 4 · SCOPE & BUDGET', questions: [
        { name: 'leadVolume', label: 'How many inbound leads per month should it handle?', options: ['Under 100', '100–500', '500–2,000', '2,000+'] },
        { name: 'budget', label: 'What is your monthly budget for this?', options: ['$500 – $1,500', '$1,500 – $3,000', '$3,000 – $5,000', '$5,000+'] },
      ] },
    ],
    success: {
      title: 'Your AI agent strategy call is next.',
      subtitle: "Our AI agent specialist will reach out within 1 hour to design your agent's qualification flow and deployment plan.",
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Vibe Coding
  '/services/vibe-coding': {
    titlePill: 'Free Project Scoping Call',
    titleSub: "Tell us what you want to build, we'll scope it and price it same day.",
    heroSubhead:
      "Custom dashboards, portals, integrations, and AI mini-apps. Tell us what you want to build and we'll scope it same day.",
    stepLabels: ['Your Info', 'About You', 'The Build', 'Budget'],
    submitLabel: 'Scope My Build',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['Agency owner', 'SaaS founder', 'Startup / product team', 'Business needing internal tools'] },
      ] },
      { eyebrow: 'STEP 3 · THE BUILD', textarea: { name: 'needsDetail', label: 'Describe what you want to build', placeholder: "E.g. 'A client portal showing Meta Ads data with hidden markup, connected to GHL...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your budget for this project?', options: ['Under $1,000', '$1,000 – $3,000', '$3,000 – $8,000', '$8,000+'] },
      ] },
    ],
    success: {
      title: "We'll scope your build on a free call.",
      subtitle: 'A vibe coding specialist will reach out within 1 hour with a clear scope, stack recommendation, and honest timeline.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Custom SaaS
  '/services/custom-saas-development': {
    titlePill: 'Free Architecture Review',
    titleSub: "Tell us what you're building, we'll design the right stack before writing code.",
    heroSubhead:
      "Multi-tenant platforms, client portals, and white-label products. Tell us what you're building and we'll design the right stack.",
    stepLabels: ['Your Info', 'Stage', 'Your Needs', 'Budget'],
    submitLabel: 'Start Architecture Review',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business / product name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR STAGE', questions: [
        { name: 'stage', label: 'What stage are you at?', options: ['Idea, nothing built yet', 'MVP in progress', 'Existing product that needs rebuilding', 'Scaling a working product'] },
      ] },
      { eyebrow: 'STEP 3 · YOUR PRODUCT', textarea: { name: 'needsDetail', label: 'Describe your product or the problem it solves', placeholder: "E.g. 'A portal where agency clients see their Meta Ads spend with multi-tenant isolation...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your budget for this build?', options: ['$2,000 – $5,000', '$5,000 – $15,000', '$15,000 – $30,000', '$30,000+'] },
      ] },
    ],
    success: {
      title: "We'll design your architecture on a free call.",
      subtitle: 'A senior developer will reach out within 1 hour to review your product requirements and map the right stack.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Figma to Code
  '/services/figma-to-code': {
    titlePill: 'Free Build Estimate',
    titleSub: "Share your Figma file details, we'll quote same day.",
    heroSubhead:
      "Pixel-perfect, production-ready code from your Figma files. Share the design and we'll send a same-day quote.",
    stepLabels: ['Your Info', 'About You', 'The Design', 'Budget'],
    submitLabel: 'Get My Build Quote',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['Web design agency', 'Freelance designer', 'Startup / founder', 'Marketing team'] },
      ] },
      { eyebrow: 'STEP 3 · THE DESIGN', textarea: { name: 'needsDetail', label: 'Share your Figma link or describe the project', placeholder: "E.g. 'figma.com/file/..., a 5-page SaaS site, desktop + mobile, built in Next.js'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your budget for this build?', options: ['Under $500', '$500 – $1,500', '$1,500 – $4,000', '$4,000+'] },
      ] },
    ],
    success: {
      title: 'Your build quote is on the way.',
      subtitle: "We'll review your Figma file details and send a same-day quote with a clear timeline and delivery format.",
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- White-Label Support
  '/services/saas-customer-support': {
    titlePill: 'Free Support Audit',
    titleSub: 'Tell us your support situation, we can be live under your brand in 5 days.',
    heroSubhead:
      "24/7 white-label support under your brand, live in 5 days. Tell us your support situation and we'll design your coverage.",
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Coverage'],
    submitLabel: 'Set Up My Support Team',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Your SaaS / agency name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['GoHighLevel SaaS founder', 'Marketing agency with retainer clients', 'Software product company', 'Agency scaling fast'] },
      ] },
      { eyebrow: 'STEP 3 · THE PROBLEM', textarea: { name: 'needsDetail', label: 'Describe your support situation', placeholder: "E.g. 'Too many tickets to keep up, clients mostly need help with automations and Twilio...'" } },
      { eyebrow: 'STEP 4 · COVERAGE & BUDGET', questions: [
        { name: 'coverage', label: 'What support coverage do you need?', options: ['Business hours only (9am–6pm)', 'Extended hours (8am–10pm)', '24/7, full round-the-clock coverage', 'Not sure yet'] },
        { name: 'budget', label: 'What is your monthly budget for support?', options: ['$300 – $800', '$800 – $2,000', '$2,000 – $5,000', '$5,000+'] },
      ] },
    ],
    success: {
      title: 'We can be live under your brand in 5 days.',
      subtitle: 'A support specialist will reach out within 1 hour to review your client base and design your coverage plan.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- App Development
  '/services/app-development': {
    titlePill: 'Free App Scoping Call',
    titleSub: "Tell us what you want to build, we'll scope it and give you a realistic timeline.",
    heroSubhead:
      "Web and mobile apps built right. Tell us what you want to build and we'll map a realistic stack and timeline.",
    stepLabels: ['Your Info', 'The App', 'Your Needs', 'Budget'],
    submitLabel: 'Scope My App',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business / app name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · THE APP', questions: [
        { name: 'appType', label: 'What type of app are you building?', options: ['Consumer mobile app (iOS + Android)', 'Business / B2B mobile app', 'Web application / SaaS product', 'Internal business tool', 'App connected to GoHighLevel'] },
      ] },
      { eyebrow: 'STEP 3 · YOUR APP IDEA', textarea: { name: 'needsDetail', label: 'Describe your app idea', placeholder: "E.g. 'A finance tracking app for iOS + Android connected to our GHL CRM, with push notifications...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your budget for this project?', options: ['$3,000 – $8,000 (MVP)', '$8,000 – $20,000', '$20,000 – $50,000', '$50,000+'] },
      ] },
    ],
    success: {
      title: 'Your app scoping call is next.',
      subtitle: 'A developer will reach out within 1 hour to review your requirements and map the right stack and timeline.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- A2P Compliance
  '/services/a2p-compliance': {
    titlePill: 'Free Compliance Check',
    titleSub: 'Tell us your messaging setup and we will identify compliance gaps.',
    heroSubhead:
      'A2P 10DLC violations shut down your texting overnight. Tell us your setup and our compliance team will map every gap.',
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Urgency'],
    submitLabel: 'Get My Compliance Plan',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'bizType', label: 'What type of business are you?', options: ['Marketing agency', 'Real estate', 'Home services', 'Healthcare / dental', 'Other'] },
      ] },
      { eyebrow: 'STEP 3 · YOUR SITUATION', textarea: { name: 'needsDetail', label: 'Describe your current messaging and compliance status', placeholder: "E.g. 'We send appointment reminders via GHL but haven’t registered our brand or campaign yet...'" } },
      { eyebrow: 'STEP 4 · URGENCY', questions: [
        { name: 'urgency', label: 'How urgent is your compliance need?', options: ['Already blocked or filtered', 'Received a warning', 'Proactive setup before launch', 'Not sure of current status'] },
      ] },
    ],
    success: {
      title: 'Your compliance review is next.',
      subtitle: 'A compliance specialist will reach out within 1 hour to audit your brand registration and campaign setup.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Voice AI
  '/services/voice-ai': {
    titlePill: 'Free Voice AI Strategy Call',
    titleSub: 'Tell us about your call flow and we will design your AI agent.',
    heroSubhead:
      'Every missed call is a lost lead. Tell us your call volume and our team will design a voice AI agent that answers, qualifies, and books.',
    stepLabels: ['Your Info', 'About You', 'Your Needs', 'Volume'],
    submitLabel: 'Design My Voice Agent',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'bizType', label: 'What type of business are you?', options: ['Home services', 'Medical / dental practice', 'Marketing agency', 'Law firm', 'Other'] },
      ] },
      { eyebrow: 'STEP 3 · CALL FLOW', textarea: { name: 'needsDetail', label: 'Describe what you need the voice agent to do', placeholder: "E.g. 'Answer after-hours calls, ask 3 qualifying questions, and book into our GHL calendar...'" } },
      { eyebrow: 'STEP 4 · CALL VOLUME', questions: [
        { name: 'callVolume', label: 'How many inbound calls per week?', options: ['Under 20', '20–50', '50–150', '150+'] },
      ] },
    ],
    success: {
      title: 'Your voice AI strategy call is next.',
      subtitle: 'A voice AI specialist will reach out within 1 hour to map your call flow and agent design.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- GHL Snapshots
  '/services/ghl-snapshots': {
    titlePill: 'Free Snapshot Review',
    titleSub: 'Tell us your agency setup and we will design your snapshot strategy.',
    heroSubhead:
      'Stop rebuilding the same GoHighLevel setup for every client. Tell us your workflow and we will build reusable snapshots.',
    stepLabels: ['Your Info', 'Your Agency', 'Your Needs', 'Scale'],
    submitLabel: 'Plan My Snapshots',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Your SaaS / agency name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR AGENCY', questions: [
        { name: 'agencyStage', label: 'Where are you in your agency journey?', options: ['Just starting with GHL', 'Running 1–5 clients', 'Scaling 6–20 clients', 'Established 20+ clients'] },
      ] },
      { eyebrow: 'STEP 3 · SNAPSHOT NEEDS', textarea: { name: 'needsDetail', label: 'What should your snapshot include?', placeholder: "E.g. 'A complete onboarding workflow, 5-stage pipeline, review request sequence, and booking calendar...'" } },
      { eyebrow: 'STEP 4 · SCALE', questions: [
        { name: 'subaccounts', label: 'How many sub-accounts will use this snapshot?', options: ['1–3 (testing)', '4–10', '11–30', '30+'] },
      ] },
    ],
    success: {
      title: 'Your snapshot strategy call is next.',
      subtitle: 'A GHL snapshot specialist will reach out within 1 hour to map your reusable account template.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- API Integrations
  '/services/api-integrations': {
    titlePill: 'Free Integration Scoping',
    titleSub: 'Tell us what you need connected and we will map the data flow.',
    heroSubhead:
      'GoHighLevel does not connect to everything out of the box. Tell us your stack and we will build the bridge.',
    stepLabels: ['Your Info', 'About You', 'The Integration', 'Timeline'],
    submitLabel: 'Scope My Integration',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['Agency owner', 'SaaS founder', 'Business owner', 'Freelancer / consultant'] },
      ] },
      { eyebrow: 'STEP 3 · THE INTEGRATION', textarea: { name: 'needsDetail', label: 'What platforms do you need connected to GoHighLevel?', placeholder: "E.g. 'Sync contacts bidirectionally between GHL and HubSpot, push deal data to Google Sheets...'" } },
      { eyebrow: 'STEP 4 · TIMELINE', questions: [
        { name: 'timeline', label: 'When do you need this live?', options: ['This week', 'Within 2 weeks', 'Within a month', 'No rush, scoping first'] },
      ] },
    ],
    success: {
      title: 'Your integration scoping call is next.',
      subtitle: 'An integration specialist will reach out within 1 hour to map your data flow and recommend the right approach.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- GHL API Custom Development
  '/services/ghl-api-custom-development': {
    titlePill: 'Free Developer Scoping Call',
    titleSub: 'Tell us what you need built and we will scope the API work.',
    heroSubhead:
      'Custom applications, marketplace apps, and platform extensions for GoHighLevel. Tell us the build and we will scope it.',
    stepLabels: ['Your Info', 'Your Stage', 'The Build', 'Budget'],
    submitLabel: 'Scope My API Build',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business / product name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR STAGE', questions: [
        { name: 'stage', label: 'What stage is this project at?', options: ['Idea, nothing built yet', 'Prototype or proof of concept', 'Existing product needs GHL integration', 'Scaling a working product'] },
      ] },
      { eyebrow: 'STEP 3 · THE BUILD', textarea: { name: 'needsDetail', label: 'Describe what you want to build with the GHL API', placeholder: "E.g. 'A marketplace app that syncs inventory data to GHL contacts and triggers workflows on purchase...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your budget for this project?', options: ['$1,000 – $3,000', '$3,000 – $8,000', '$8,000 – $20,000', '$20,000+'] },
      ] },
    ],
    success: {
      title: 'Your developer scoping call is next.',
      subtitle: 'A senior GHL API developer will reach out within 1 hour to review your requirements and map the build.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- GHL Ads Management
  '/services/ghl-ads-management': {
    titlePill: 'Free Ads Pipeline Audit',
    titleSub: 'Tell us your ad setup and we will show you where leads are leaking.',
    heroSubhead:
      'Your ads generate clicks but leads disappear before follow-up. Tell us your setup and we will close the loop.',
    stepLabels: ['Your Info', 'About You', 'Your Ads', 'Ad Spend'],
    submitLabel: 'Audit My Ads Pipeline',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT(), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · ABOUT YOU', questions: [
        { name: 'role', label: 'What best describes you?', options: ['Agency managing client ads', 'Business running own ads', 'Franchise or multi-location', 'Starting paid ads soon'] },
      ] },
      { eyebrow: 'STEP 3 · YOUR ADS', textarea: { name: 'needsDetail', label: 'Describe your current ad setup and what is not working', placeholder: "E.g. 'Running Facebook lead ads but leads sit in a spreadsheet, no follow-up automation in GHL...'" } },
      { eyebrow: 'STEP 4 · MONTHLY AD SPEND', questions: [
        { name: 'adSpend', label: 'What is your monthly ad spend?', options: ['Under $1,000', '$1,000 – $5,000', '$5,000 – $20,000', '$20,000+'] },
      ] },
    ],
    success: {
      title: 'Your ads pipeline audit is next.',
      subtitle: 'An ads integration specialist will reach out within 1 hour to map your lead flow and fix the gaps.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- White-Label Support
  '/services/white-label-support': {
    titlePill: 'Free White-Label Consult',
    titleSub: 'Tell us your client base and we will design your white-label coverage.',
    heroSubhead:
      'Your clients need GoHighLevel help but your team is maxed out. Tell us your situation and we will cover them under your brand.',
    stepLabels: ['Your Info', 'Your Agency', 'Support Needs', 'Coverage'],
    submitLabel: 'Design My Coverage Plan',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Your SaaS / agency name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR AGENCY', questions: [
        { name: 'agencySize', label: 'How many clients do you support?', options: ['1–5 clients', '6–15 clients', '16–50 clients', '50+ clients'] },
      ] },
      { eyebrow: 'STEP 3 · SUPPORT NEEDS', textarea: { name: 'needsDetail', label: 'What kind of support do your clients need?', placeholder: "E.g. 'Clients need help with workflows, funnels, and Twilio issues. We want Slack-based escalation...'" } },
      { eyebrow: 'STEP 4 · COVERAGE', questions: [
        { name: 'coverage', label: 'What support hours do you need?', options: ['Business hours only (9am–6pm)', 'Extended hours (8am–10pm)', '24/7 full coverage', 'Not sure yet'] },
        { name: 'budget', label: 'What is your monthly budget for support?', options: ['$500 – $1,500', '$1,500 – $3,000', '$3,000 – $6,000', '$6,000+'] },
      ] },
    ],
    success: {
      title: 'Your white-label coverage plan is next.',
      subtitle: 'A support specialist will reach out within 1 hour to design your branded coverage and onboard your team.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- SaaS CRM Launch
  '/services/saas-crm': {
    titlePill: 'Free SaaS Launch Review',
    titleSub: 'Tell us your SaaS plans and we will map your launch configuration.',
    heroSubhead:
      'Launching GoHighLevel SaaS Mode without the right setup burns your margin. Tell us your plans and we will configure it properly.',
    stepLabels: ['Your Info', 'Your Stage', 'SaaS Plans', 'Timeline'],
    submitLabel: 'Plan My SaaS Launch',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Your SaaS / agency name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR STAGE', questions: [
        { name: 'stage', label: 'Where are you with GHL SaaS Mode?', options: ['Have not started yet', 'Enabled but not configured', 'Live but losing money on margins', 'Scaling and need optimization'] },
      ] },
      { eyebrow: 'STEP 3 · YOUR PLANS', textarea: { name: 'needsDetail', label: 'Describe your SaaS product and pricing goals', placeholder: "E.g. 'Want to offer 3 pricing tiers for agencies, need Stripe rebilling, custom onboarding snapshot...'" } },
      { eyebrow: 'STEP 4 · TIMELINE', questions: [
        { name: 'timeline', label: 'When do you want to launch?', options: ['This week', 'Within 2 weeks', 'Within a month', 'Still planning'] },
      ] },
    ],
    success: {
      title: 'Your SaaS launch review is next.',
      subtitle: 'A SaaS configuration specialist will reach out within 1 hour to review your pricing, margins, and provisioning setup.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- GHL Experts for Agencies
  '/services/ghl-experts-for-agencies': {
    titlePill: 'Free Agency Strategy Call',
    titleSub: 'Tell us what your agency needs and we will match the right experts.',
    heroSubhead:
      'Your agency needs certified GoHighLevel specialists without the overhead of full-time hires. Tell us what you need.',
    stepLabels: ['Your Info', 'Your Agency', 'Your Needs', 'Budget'],
    submitLabel: 'Match My Expert Team',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Your SaaS / agency name'), questions: [HOW_FOUND_QUESTION] },
      { eyebrow: 'STEP 2 · YOUR AGENCY', questions: [
        { name: 'agencySize', label: 'How big is your agency?', options: ['Solo operator', '2–5 team members', '6–15 team members', '15+ team members'] },
      ] },
      { eyebrow: 'STEP 3 · WHAT YOU NEED', textarea: { name: 'needsDetail', label: 'Describe the GHL work you need help with', placeholder: "E.g. 'Need someone to build funnels, set up automations, and handle client onboarding in GHL...'" } },
      { eyebrow: 'STEP 4 · BUDGET', questions: [
        { name: 'budget', label: 'What is your monthly budget for GHL support?', options: ['$500 – $1,500', '$1,500 – $3,000', '$3,000 – $6,000', '$6,000+'] },
      ] },
    ],
    success: {
      title: 'Your agency strategy call is next.',
      subtitle: 'A senior GHL specialist will reach out within 1 hour to understand your workflow and match the right experts.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Business owners
  // Added 2026-10-29 (owner): same webhook and payload as the service forms;
  // `service` is sent as /for-business-owners.
  '/for-business-owners': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how customers reach you today. We'll show you where leads slip through.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to a new enquiry today?', placeholder: 'e.g. calls go to voicemail after 5pm and web forms get a reply the next morning...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how enquiries reach you today and show you where leads slip through, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------------------- Agency owners
  // Added 2026-10-29 (owner): same webhook and payload as the service forms;
  // `service` is sent as /for-agency-owners.
  '/for-agency-owners': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us about your agency. We'll show you what we can take off your plate.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Agency name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What would you hand over first?', placeholder: 'e.g. new client setups and A2P registration, about 5 new accounts a month...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will review your client workload and map what we can take on under your brand, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ================================================================ Industries
  // Added 2026-10-29 (owner): same webhook and payload as the service forms;
  // `service` is sent as the industry page's path.

  // ---------------------------------------------------------- Dental
  '/industries/dental': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how patients book today. We'll show you where appointments slip through.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Practice name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens when a patient calls or books today?', placeholder: 'e.g. after-hours calls go to voicemail and recall reminders are sent by hand...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how patients reach your practice and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // --------------------------------------------------- Home services
  '/industries/home-services': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how jobs come in today. We'll show you where leads slip through.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to a new job request today?', placeholder: "e.g. calls are missed while we're on site and quotes are followed up by hand..." } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how job requests reach you and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ------------------------------------------------------------ HVAC
  '/industries/hvac': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how service calls come in today. We'll show you where jobs slip through.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Company name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to a service call after hours?', placeholder: 'e.g. emergency calls go to voicemail at night and maintenance reminders never go out...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how service calls reach you and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ------------------------------------------------------- Law firms
  '/industries/law': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how new enquiries reach your firm. We'll show you where intake slows down.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Firm name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'How does intake work today?', placeholder: 'e.g. web enquiries wait until morning and intake forms go back and forth by email...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how new enquiries reach your firm and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ----------------------------------------------------- Real estate
  '/industries/real-estate': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how leads reach you today. We'll show you where they go cold.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Brokerage / team name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to a new lead today?', placeholder: 'e.g. portal leads wait hours for a reply and follow-up stops after one call...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how leads reach you and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ================================================================ More industries
  // Added 2026-11-04 (owner): same webhook and payload as the service forms.

  // -------------------------------------------------------- Med spas
  '/industries/med-spa': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how clients book today. We'll show you where rebookings slip.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Clinic name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens when a client enquires or finishes a treatment?', placeholder: 'e.g. DMs get answered hours later and nobody reminds clients to rebook...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at how clients book and rebook at your clinic and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // --------------------------------------------------------- Roofing
  '/industries/roofing': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how leads reach you today. We'll show you where jobs slip away.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Company name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to a lead after the estimate goes out?', placeholder: 'e.g. we send the estimate, then nobody follows up until the homeowner calls back...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at your lead and estimate follow-up and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ------------------------------------------------------- Tree care
  '/industries/tree-care': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how calls reach you today. We'll show you what you're missing.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Company name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to calls while your crew is working?', placeholder: 'e.g. calls go to voicemail until the end of the day and pruning customers never get reminded...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at your calls, bookings and repeat work and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ---------------------------------------------------- Tree removal
  '/industries/tree-removal': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how emergency and quote calls reach you. We'll show you what you're missing.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Company name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to calls at night or after a storm?', placeholder: 'e.g. emergency calls go to voicemail overnight and quotes are followed up by hand...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at your call handling and quote follow-up and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ----------------------------------------------------- Landscaping
  '/industries/landscaping': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how leads and contracts work today. We'll show you where they slip.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Company name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What happens to estimate requests and maintenance renewals today?', placeholder: 'e.g. spring leads wait days for a callback and contracts lapse without a reminder...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at your leads, quotes and contract renewals and map the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ----------------------------------------------------- AI agencies
  '/industries/ai-agencies': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us what you sell. We'll show you how we'd build and support it.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Agency name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'Which AI agents or automations do you sell to clients?', placeholder: 'e.g. AI receptionists for dental clinics, about 4 new clients a month...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel and AI specialist will review your offer and plan the first client build with you, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // --------------------------------------------------- Entrepreneurs
  '/industries/entrepreneurs': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us what you sell. We'll suggest the smallest setup that helps.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'What do you want GoHighLevel to handle for you?', placeholder: 'e.g. a landing page, booking calls, taking payment and follow-up emails...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will look at your business and suggest the smallest setup that helps, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },

  // ------------------------------------------------ Business coaches
  '/industries/business-coaches': {
    titlePill: 'Free Consultation',
    titleSub: "Tell us how clients find you. We'll show you how to fill your calendar.",
    stepLabels: ['Your Info', 'Your Needs'],
    submitLabel: 'Book My Free Call',
    steps: [
      { eyebrow: 'STEP 1 · WHO ARE YOU?', fields: CONTACT('Business name') },
      { eyebrow: 'STEP 2 · WHAT DO YOU NEED?', textarea: { name: 'needsDetail', label: 'How do prospects become clients today?', placeholder: 'e.g. leads come from my podcast, I book calls by email and send payment links by hand...' } },
    ],
    success: {
      title: "Great, we'll be in touch within 1 hour.",
      subtitle: 'A GoHighLevel specialist will map your funnel, booking and payments and suggest the fixes, on a free call.',
      ctaLabel: 'Book a Call Now', ctaTo: '/booking',
    },
  },
}

// The one agency page that had no form of its own uses the agency form above
// (owner, 2026-11-04: a form at the top of every page, "don't use any new
// form"). Same fields and webhook; `service` is sent as the page's own slug,
// and the page keeps its own hero text. The other formless agency pages
// redirect elsewhere (redirects.phase0.ts).
SERVICE_FORMS['/services/cost-effective-ghl-team'] = {
  ...SERVICE_FORMS['/services/ghl-experts-for-agencies'],
  heroSubhead: undefined,
}
